import {
    isOptionOrderViewModel,
    OrderViewModel,
    TmcOrderLegViewModel,
    TmcOrderViewModel
} from "../../../models/OrderViewModel";
import {AccountBriefRecord} from "../../../../account/types/AccountBriefRecord";
import {IsoDate, OffsetDateTime} from "../../../../pseudo-types/DateTypes";
import {Exchange} from "../../../../instruments/exchange/Exchange";
import {
    OrderDestination,
    OrderLifetime,
    OrderNumber,
    OrderSide,
    OrderState,
    OrderStatus
} from "../../../models/OrderRecord";
import {PriceStrategy} from "../../../../components/inputs/price/input/PriceStrategy";
import {QuoteViewModel} from "../../../../quote/models/QuoteViewModel";
import {isConditionalIressOrderViewModel, isIressOrderModel} from "../../../models/iress/IressOrderViewModel";
import {
    isConditionalSimulatedOrderViewModel,
    isSimulatedOrderModel
} from "app/orders/models/simulated/SimulatedOrderViewModel";
import {PricingInstruction} from "../../../models/PricingInstruction";
import {OptionType} from "../../../models/OrderResponse";
import {OrderConditions} from "../../../models/OrderConditions";

export type OrderListViewModels = ReadonlyArray<OrderListViewModel>;

/**
 * Wraps an OrderViewModel into a very similar model that has a couple of convenience methods and a "visible"
 * property.
 * This is due to the potential large size of the order list, where the render/re-render time was large and therefore
 * filtering the table rows as we normally would was causing massive performance issues.
 * Thus we instead set the display value of the row to show/hide instead to speed up the filtering render time.
 */
export class OrderListViewModel implements OrderViewModel {
    public readonly orderIdentifier: string;

    protected constructor(public readonly order: OrderViewModel, public readonly visible: boolean) {
        this.orderIdentifier = OrderListViewModel.createOrderIdentifier(order);
    }

    public get parentOrderLimit(): number {
        return this.order.parentOrderLimit;
    }

    public get uncommittedVolume(): number {
        return this.order.uncommittedVolume;
    }

    public get isInternal(): boolean {
        return this.order.isInternal;
    }

    private static createOrderIdentifier(order: OrderViewModel): string {
        if (isIressOrderModel(order)) {
            return `IRESS:${order.serverName}:${order.orderNumber}`;
        } else if (isSimulatedOrderModel(order)) {
            return `SIMULATED:${order.id}`;
        } else {
            throw Error(`Unable to make identifier for model type ${typeof order}`);
        }
    }

    public get orderNumber(): OrderNumber {
        if (isIressOrderModel(this.order)) {
            return this.order.orderNumber;
        } else if (isSimulatedOrderModel(this.order)) {
            return this.order.id as any as OrderNumber;
        } else {
            throw Error(`Unable to extract order number for model type ${this.order.constructor.name}`);
        }
    }
    public get parentOrderNumber(): number {
        return this.order.parentOrderNumber;
    }

    public get account(): AccountBriefRecord | null {
        return this.order.account;
    }

    public get actionStatus(): string {
        return this.order.actionStatus;
    }

    public get stateDescription(): string {
        return this.order.stateDescription;
    }

    public get averagePrice(): number {
        return this.order.averagePrice;
    }

    public get code(): string {
        return this.order.code;
    }

    public get created(): OffsetDateTime {
        return this.order.created;
    }

    public get cvf(): number {
        return this.order.cvf;
    }

    public get destination(): OrderDestination {
        return this.order.destination;
    }

    public get exchange(): Exchange {
        return this.order.exchange;
    }

    public get filledQuantity(): number {
        return this.order.filledQuantity;
    }

    public get hasQuote(): boolean {
        return this.order.hasQuote;
    }

    public get isAmendable(): boolean {
        return this.order.isAmendable;
    }

    public get isCancelled(): boolean {
        return this.order.isCancelled;
    }

    public get isCompleted(): boolean {
        return this.order.isCompleted;
    }

    public get isFailed(): boolean {
        return this.order.isFailed;
    }

    public get isFilled(): boolean {
        return this.order.isFilled;
    }

    public get isOpen(): boolean {
        return this.order.isOpen;
    }

    public get isBuy(): boolean {
        return this.order.isBuy;
    }

    public get isSell(): boolean {
        return this.order.isSell;
    }

    public get isMine(): boolean {
        return this.order.isMine;
    }

    public get lastAction(): string {
        return this.order.lastAction;
    }

    public get lifetime(): OrderLifetime {
        return this.order.lifetime;
    }

    public get limit(): number | null {
        return this.order.limit;
    }

    public get priceStrategy(): PriceStrategy {
        return this.order.priceStrategy;
    }

    public get pricingInstruction(): PricingInstruction {
        return this.order.pricingInstruction;
    }

    public get quantity(): number {
        return this.order.quantity;
    }

    public get quote(): QuoteViewModel | null {
        return this.order.quote;
    }

    public get remainingQuantity(): number {
        return this.order.remainingQuantity;
    }

    public get side(): OrderSide {
        return this.order.side;
    }

    public get state(): OrderState {
        return this.order.state;
    }

    public get status(): OrderStatus {
        return this.order.status;
    }

    public get type(): OptionType | null {
        return this.order.type;
    }

    public get theo(): number | null {
        return this.order.theo;
    }

    public get delta(): number | null {
        return this.order.delta;
    }

    public get vol(): number | null {
        return this.order.vol;
    }

    public get expiry(): IsoDate | null {
        return this.order.expiry;
    }

    public get strike(): number | null {
        return this.order.strike;
    }

    public get tradedPercent(): number {
        return this.order.tradedPercent;
    }

    public get updated(): OffsetDateTime {
        return this.order.updated;
    }

    public get validUntil(): OffsetDateTime | null {
        return this.order.validUntil;
    }

    public get isTmc(): boolean {
        return false;
    }

    public get isConditional(): boolean {
        return this.conditions != null;
    }

    public get conditions(): OrderConditions | null {
        if (isIressOrderModel(this.order) && isConditionalIressOrderViewModel(this.order)) {
            return this.order.conditions;
        }
        if (isSimulatedOrderModel(this.order) && isConditionalSimulatedOrderViewModel(this.order)) {
            return this.order.conditions;
        }
        return null;
    }

    public get hasStrike(): boolean {
        return isOptionOrderViewModel(this.order) && (this.order.strike != null);
    }

    public static of(order: OrderViewModel, visible: boolean): OrderListViewModel {
        return new OrderListViewModel(order, visible);
    }
}

export class TmcOrderListViewModel extends OrderListViewModel {

    public readonly legs: ReadonlyArray<TmcOrderLegViewModel>;

    private constructor(order: TmcOrderViewModel, visible: boolean) {
        super(order, visible);
        this.legs = order.legs;
    }

    public get isTmc(): boolean {
        return true;
    }

    public static of(order: TmcOrderViewModel, visible: boolean): TmcOrderListViewModel {
        return new TmcOrderListViewModel(order, visible);
    }
}
